/**
 * Collection-page filter enhancements. Light-DOM, self-guarded, all optional —
 * the form submits via its Apply button without JS.
 *
 * <facet-filters> — auto-submits on change on DESKTOP only. On mobile the facets
 * live in a drawer (<details class="facets-drawer">) where auto-submit would
 * reload mid-multi-select (mobile needs an explicit apply), so changes
 * instead update the apply button with a live result count fetched via the
 * Section Rendering API. Also: per-facet value search (inputs are nameless, so
 * they never submit), Escape/backdrop close for the drawer, and a dataLayer
 * `filter_applied` event per toggle when analytics is enabled.
 *
 * The saved trailer's own filter used to live here as <trailer-filter>. It moved to
 * assets/trailer-filter.js when it stopped being a view filter over the page in front of
 * it: it now resolves the trailer's parts IN this category from the saved index and asks
 * the server for those cards, which is a different seam from facet plumbing.
 */
(() => {
  const DESKTOP = '(min-width: 750px)';

  if (!customElements.get('facet-filters')) {
    customElements.define(
      'facet-filters',
      class FacetFilters extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          if (!this.form) return;
          this.classList.add('facets--js');
          this.drawer = this.querySelector('.facets-drawer');
          if (this.drawer) {
            // Server renders the drawer open (closed-<details> content cannot
            // be revealed by author CSS). JS takes over: stamp data-js so the
            // mobile bottom-sheet styles apply, close on mobile, and keep the
            // open state in sync when the viewport crosses the breakpoint.
            this.drawer.dataset.js = '';
            this.mq = window.matchMedia(DESKTOP);
            this.onBreakpoint = () => {
              this.drawer.open = this.mq.matches;
            };
            this.onBreakpoint();
            this.mq.addEventListener('change', this.onBreakpoint);
          }
          this.applyBtn = this.querySelector('.facets__apply');
          this.applyLabel = this.applyBtn ? this.applyBtn.textContent : '';
          this.countTpl = this.dataset.showResults || '';
          this.abort = null;

          this.onChange = (event) => {
            const t = event.target;
            if (!t) return;
            if (t.type === 'search') return; // facet value search, never submits
            if (t.type === 'number') return; // paired price inputs → Apply button
            this.track(t);
            // The mobile hold-back exists because the drawer lets a shopper set several
            // facets before applying. It should cover the controls IN the drawer
            // and nothing else: the sort select sits up in the bar, where there is no Apply
            // to press, so holding it back left it doing nothing at all on a phone.
            const inDrawer = this.drawer && this.drawer.contains(t);
            if (!inDrawer || window.matchMedia(DESKTOP).matches) {
              this.form.submit();
            } else {
              this.updateCount();
            }
          };
          this.onInput = (event) => {
            const t = event.target;
            if (t && t.type === 'search' && t.hasAttribute('data-facet-search')) this.sift(t);
          };
          this.onKeydown = (event) => {
            if (event.key === 'Escape' && this.drawer && this.drawer.open) {
              this.drawer.open = false;
              const summary = this.drawer.querySelector('summary');
              if (summary) summary.focus();
            }
          };
          this.form.addEventListener('change', this.onChange);
          this.form.addEventListener('input', this.onInput);
          this.addEventListener('keydown', this.onKeydown);
        }

        disconnectedCallback() {
          if (!this.form) return;
          this.form.removeEventListener('change', this.onChange);
          this.form.removeEventListener('input', this.onInput);
          this.removeEventListener('keydown', this.onKeydown);
          if (this.mq) this.mq.removeEventListener('change', this.onBreakpoint);
          if (this.abort) this.abort.abort();
        }

        // Live "Show N results" on the mobile apply button, via the section's own
        // rendered count. Best effort — a failed fetch just keeps the plain label.
        async updateCount() {
          if (!this.applyBtn || !this.countTpl) return;
          if (this.abort) this.abort.abort();
          this.abort = new AbortController();
          try {
            const params = new URLSearchParams(new FormData(this.form));
            const sectionId = this.dataset.sectionId;
            const url = `${window.location.pathname}?${params}&section_id=${sectionId}`;
            const res = await fetch(url, { signal: this.abort.signal });
            if (!res.ok) return;
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            const count = doc.querySelectorAll('.collection__grid .product-card').length;
            const more = doc.querySelector('.collection__pagination') ? '+' : '';
            const label = this.applyBtn.querySelector('.btn__label') || this.applyBtn;
            label.textContent = this.countTpl.replace('%COUNT%', count + more);
          } catch (e) {
            // aborted or offline — keep the current label
          }
        }

        // Filter a facet's value list on typed text (both the visible list and
        // the folded "show more" list; the fold opens while searching).
        sift(input) {
          const facet = input.closest('.facet');
          if (!facet) return;
          const q = input.value.trim().toLowerCase();
          const more = facet.querySelector('.facet__more');
          if (more && q) more.open = true;
          facet.querySelectorAll('.facet__list li').forEach((li) => {
            const label = li.querySelector('.facet__option-label');
            const hit = !q || (label && label.textContent.toLowerCase().includes(q));
            li.hidden = !hit;
          });
        }

        track(input) {
          if (!window.dataLayer || input.type !== 'checkbox') return;
          const facet = input.closest('.facet');
          const summary = facet && facet.querySelector('.facet__summary span');
          window.dataLayer.push({
            event: 'filter_applied',
            filter_name: summary ? summary.textContent.trim() : input.name,
            filter_value: input.value,
            filter_active: input.checked,
          });
        }
      }
    );
  }
})();
